import { useCallback } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { GlobeCanvas } from '../scene/GlobeCanvas';
import { useReducedMotion } from '../hooks/useReducedMotion';
import { LOCATIONS } from '../data/locations';
import { PlaceNavigator } from './PlaceIndex';
import { SiteHeader } from './SiteHeader';

/** Permanent app shell: a top bar, an always-mounted globe pane, and an
 *  editorial panel (the routed <Outlet/>). Two columns on desktop, stacked on
 *  mobile. The canvas is never resized on selection — the layout is fixed. */
export function MemoryLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const reducedMotion = useReducedMotion();

  const slug = pathname.match(/^\/city\/([^/]+)/)?.[1] ?? null;

  const onSelect = useCallback(
    (next: string) => {
      navigate(`/city/${next}`, { replace: !!slug });
    },
    [navigate, slug],
  );

  // Selecting a city hands most of the screen to the photographs: the globe
  // drops to a left rail (desktop) or a short strip (mobile). Split happens at
  // lg; below that it stacks. The panel width is viewport-derived, not capped,
  // so the gallery grows with the screen instead of staying a thin strip.
  const layout = slug
    ? 'grid-rows-[clamp(200px,26dvh,300px)_1fr] lg:grid-rows-1 lg:grid-cols-[clamp(360px,26vw,460px)_minmax(0,1fr)]'
    : 'grid-rows-[300px_1fr] lg:grid-rows-1 lg:grid-cols-[1fr_clamp(360px,30vw,460px)]';

  return (
    <div className="grid h-[100dvh] w-full grid-rows-[auto_1fr] overflow-hidden bg-[#05070c] text-white">
      <SiteHeader action={{ to: '/list', label: 'View as list' }} />

      <main className={`grid min-h-0 ${layout}`}>
        <div className="flex min-h-0 flex-col">
          <section
            aria-label={`Interactive globe of ${LOCATIONS.length} travel locations`}
            aria-describedby="globe-instructions"
            className={`relative min-h-0 flex-1 [touch-action:none] [background:radial-gradient(120%_100%_at_50%_35%,#0d1524_0%,#05070c_70%)] ${slug ? 'lg:flex-[0_0_62%]' : ''}`}
          >
            <p id="globe-instructions" className="sr-only">
              Drag to rotate and scroll to zoom the globe, then select a city. The globe itself is not keyboard
              navigable; every place is also linked from the Places list on the home view, from the index beside an
              open city and from “View as list”.
            </p>
            <GlobeCanvas selectedSlug={slug} onSelect={onSelect} reducedMotion={reducedMotion} />
          </section>
          {slug && <PlaceNavigator current={slug} />}
        </div>

        <aside className="min-h-0 overflow-y-auto overflow-x-hidden border-t border-white/10 [touch-action:pan-y] lg:border-l lg:border-t-0">
          <Outlet />
        </aside>
      </main>
    </div>
  );
}
